import { sendCreativeChatMessageStreaming } from "@/screens/creatives/apiService";
import { MessageType, Agent } from "@/components/common/copilot/types";
import { ChatDataConfig } from "@/screens/creatives/types";
import { useGlobalStore } from "@/GlobalStore";

export interface StreamState {
  id: string;
  chatId: string | null;
  accountId: string;
  reportId: string;
  status: "loading" | "in_progress" | "completed" | "error";
  messages: MessageType[];
  error: string | null;
  abortController: AbortController;
  onUpdate: (
    chatId?: string,
    messages?: MessageType[],
    status?: string,
    error?: string | null,
  ) => void;
  onComplete: (chatId: string) => void;
  onStart: (chatId: string) => void;
}

class BackgroundStreamManager {
  private streams: Map<string, StreamState> = new Map();
  private listeners: Map<string, Set<(stream: StreamState) => void>> = new Map();

  constructor() {
    // Access to global store - it's a singleton so this is safe
  }

  /**
   * Start a new background stream
   */
  async startStream(
    streamId: string,
    accountId: string,
    reportId: string,
    data: ChatDataConfig,
    onUpdate: (
      chatId?: string,
      messages?: MessageType[],
      status?: string,
      error?: string | null,
    ) => void,
    onComplete: (chatId: string) => void,
    initialMessages: MessageType[] = [],
    onStart: (chatId: string) => void,
  ): Promise<void> {
    // Cancel any existing stream with the same ID
    this.cancelStream(streamId);

    const abortController = new AbortController();

    const streamState: StreamState = {
      id: streamId,
      chatId: data.chat_id || null,
      accountId,
      reportId,
      status: "loading",
      messages: initialMessages,
      error: null,
      abortController,
      onUpdate,
      onComplete,
      onStart,
    };

    this.streams.set(streamId, streamState);
    useGlobalStore.getState().addStream(streamId, streamState);
    this.notifyListeners(streamId, streamState);

    try {
      const response = await sendCreativeChatMessageStreaming(
        accountId,
        data,
        abortController.signal,
      );
      onStart?.(data.chat_id || "");

      if (!response?.ok) {
        throw new Error(`Server responded with status: ${response?.status || "unknown"}`);
      }

      await this.handleStreaming(streamId, response);
    } catch (error) {
      console.error("Stream error:", error);
      if (error instanceof Error && error.name !== "AbortError") {
        this.updateStreamState(streamId, {
          status: "error",
          error: "An error has occurred. Please try again.",
        });
      }
    }
  }

  /**
   * Handle the streaming response
   */
  private async handleStreaming(streamId: string, response: Response): Promise<void> {
    const streamState = this.streams.get(streamId);
    if (!streamState) return;

    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder("utf-8");
    let streamingResponse = "";
    let streamingReasoning = "";
    let streamingAnswer = "";
    let chatIdFromResp: string;
    let assistantChatConversationIdFromResp: string | null = null;
    let time_taken: number | null = null;

    this.updateStreamState(streamId, { status: "in_progress" });

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.replace("data: ", "");
            if (data !== "[DONE]") {
              try {
                const jsonData: MessageType = JSON.parse(data) as MessageType;
                const status = jsonData.status;
                const annotations = jsonData.annotations;

                // Update messages with annotations
                if (annotations) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    for (const annotation of annotations) {
                      const {
                        text: annotationText,
                        file_link: annotationLink,
                        type: annotationType,
                      } = annotation;

                      if (annotationType === "text_annotation") {
                        const annotationReplaceIndex = updatedMessages.findIndex(
                          (message) =>
                            message.agent === Agent.assistant &&
                            message.response?.includes(annotationText),
                        );

                        if (annotationReplaceIndex !== -1) {
                          updatedMessages[annotationReplaceIndex].response =
                            updatedMessages[annotationReplaceIndex].response.replace(
                              annotationText,
                              annotationLink,
                            );
                        }
                      } else {
                        const latestMessage = updatedMessages.find(
                          (msg) => msg.agent === Agent.assistant,
                        );
                        if (latestMessage) {
                          if (latestMessage.annotations) {
                            latestMessage.annotations.push(annotation);
                          } else {
                            latestMessage.annotations = [annotation];
                          }
                        }
                      }
                    }
                    return updatedMessages;
                  });
                }

                // Update conversation IDs
                if (jsonData.user_chat_conversation_id) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.user_chat_conversation_id =
                        jsonData.user_chat_conversation_id;
                    }
                    return updatedMessages;
                  });
                }
                if (jsonData.assistant_chat_conversation_id) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.assistant_chat_conversation_id =
                        jsonData.assistant_chat_conversation_id;
                    }
                    return updatedMessages;
                  });
                }

                // Update report data
                if (jsonData.report) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.report = jsonData.report;
                    }
                    return updatedMessages;
                  });
                }

                // Update sections data
                if (jsonData.sections) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.sections = jsonData.sections || [];
                    }
                    return updatedMessages;
                  });
                }

                if (jsonData?.metrics) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.metrics = jsonData?.metrics || [];
                    }
                    return updatedMessages;
                  });
                }

                // Update creatives data
                if (jsonData?.creatives) {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.creatives = jsonData?.creatives || [];
                    }
                    return updatedMessages;
                  });
                }

                // Handle different statuses
                if (status === "loading") {
                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.status = "loading";
                      lastMessage.text = jsonData.text;
                    }
                    return updatedMessages;
                  });
                } else if (status === "in_progress") {
                  // Handle different content types
                  if (jsonData.type === "reasoning") {
                    streamingReasoning += jsonData.text || "";
                    if (jsonData.done) {
                      streamingReasoning += "\n";
                    }
                  } else if (jsonData.type === "answer") {
                    streamingAnswer += jsonData.text || "";
                  } else {
                    streamingResponse += jsonData.text || "";
                  }

                  this.updateStreamMessages(streamId, (prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessage = updatedMessages.find(
                      (msg) =>
                        msg.agent === Agent.assistant &&
                        msg.assistant_chat_conversation_id === null,
                    );
                    if (lastMessage) {
                      lastMessage.response = streamingResponse;
                      lastMessage.reasoning = streamingReasoning;
                      lastMessage.answer = streamingAnswer;
                      lastMessage.status = status;
                      lastMessage.text = jsonData.text;
                      lastMessage.type = jsonData.type;
                    }
                    return updatedMessages;
                  });
                } else if (status === "completed") {
                  this.updateStreamState(streamId, { status: "completed" });
                } else if (status === "error") {
                  this.updateStreamState(streamId, {
                    status: "error",
                    error: jsonData.text ?? "An error occurred",
                  });
                }

                chatIdFromResp = jsonData?.chat_id ?? "";
                assistantChatConversationIdFromResp =
                  jsonData?.assistant_chat_conversation_id ?? null;
                time_taken = jsonData?.time_taken;

                if (chatIdFromResp) {
                  this.updateStreamState(streamId, { chatId: chatIdFromResp });
                }
              } catch (e) {
                console.error("Error parsing JSON:", e);
              }
            }
          }
        }
      }
    } finally {
      // Finalize the stream
      this.updateStreamMessages(streamId, (prevMessages) => {
        return prevMessages.map((msg) => ({
          ...msg,
          assistant_chat_conversation_id:
            msg.assistant_chat_conversation_id || assistantChatConversationIdFromResp,
          time_taken: msg.time_taken || time_taken,
          status: "completed" as const,
        }));
      });

      const finalState = this.streams.get(streamId);
      if (finalState) {
        this.updateStreamState(streamId, { status: "completed" });
        if (finalState.chatId) {
          finalState.onComplete(finalState.chatId);
        }
      }
    }
  }

  /**
   * Update stream messages
   */
  private updateStreamMessages(
    streamId: string,
    updater: (messages: MessageType[]) => MessageType[],
  ): void {
    const streamState = this.streams.get(streamId);
    if (!streamState) return;

    const updatedMessages = updater(streamState.messages);
    this.updateStreamState(streamId, { messages: updatedMessages });
  }

  /**
   * Update stream state
   */
  private updateStreamState(streamId: string, updates: Partial<StreamState>): void {
    const streamState = this.streams.get(streamId);
    if (!streamState) return;

    Object.assign(streamState, updates);

    // Update global state
    useGlobalStore.getState().addStream(streamId, streamState);

    this.notifyListeners(streamId, streamState);
    if (updates.chatId)
      streamState.onUpdate(updates.chatId, undefined, undefined, streamState.error);
  }

  /**
   * Cancel a specific stream
   */
  cancelStream(streamId: string): void {
    const streamState = this.streams.get(streamId);
    if (streamState) {
      streamState.abortController.abort();
      this.streams.delete(streamId);
      useGlobalStore.getState().removeStream(streamId);
      this.listeners.delete(streamId);
    }
  }

  /**
   * Cancel all streams
   */
  cancelAllStreams(): void {
    for (const [, streamState] of this.streams) {
      streamState.abortController.abort();
    }
    this.streams.clear();
    useGlobalStore.getState().clearAllStreams();
    this.listeners.clear();
  }

  /**
   * Get stream state
   */
  getStreamState(streamId: string): StreamState | undefined {
    return this.streams.get(streamId);
  }

  /**
   * Get all active streams
   */
  getAllStreams(): StreamState[] {
    return Array.from(this.streams.values());
  }

  /**
   * Check if a stream is active
   */
  isStreamActive(streamId: string): boolean {
    return this.streams.has(streamId);
  }

  /**
   * Add a listener for stream updates
   */
  addListener(streamId: string, listener: (stream: StreamState) => void): void {
    if (!this.listeners.has(streamId)) {
      this.listeners.set(streamId, new Set());
    }
    this.listeners.get(streamId)!.add(listener);
  }

  /**
   * Remove a listener
   */
  removeListener(streamId: string, listener: (stream: StreamState) => void): void {
    const listeners = this.listeners.get(streamId);
    if (listeners) {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.listeners.delete(streamId);
      }
    }
  }

  /**
   * Notify listeners of stream updates
   */
  private notifyListeners(streamId: string, streamState: StreamState): void {
    const listeners = this.listeners.get(streamId);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(streamState);
        } catch (error) {
          console.error("Error in stream listener:", error);
        }
      });
    }

    // Zustand will automatically notify subscribers when the state updates
  }

  /**
   * Clean up completed streams
   */
  cleanupCompletedStreams(): void {
    for (const [streamId, streamState] of this.streams) {
      if (streamState.status === "completed" || streamState.status === "error") {
        this.streams.delete(streamId);
        useGlobalStore.getState().removeStream(streamId);
        this.listeners.delete(streamId);
      }
    }
  }
}

// Export singleton instance
// Create a single global instance that persists across component mounts/unmounts
export const backgroundStreamManager = new BackgroundStreamManager();

// Make sure the singleton persists in global scope
if (typeof window !== "undefined") {
  (window as any).__backgroundStreamManager = backgroundStreamManager;
}

// Clean up completed streams every 5 minutes
setInterval(
  () => {
    backgroundStreamManager.cleanupCompletedStreams();
  },
  5 * 60 * 1000,
);
